/**
 * Storefront logo generator.
 *
 * Works on any box that has the Carrier CLI or MCP — no image-API key required.
 * Produces a distinctive SVG mark from brand name + accent.
 */

export type LogoMark = {
  svg: string;
  filename: string;
  source: "svg";
  pngBase64?: string;
};

function clampHex(hex: string, fallback: string): string {
  const h = hex.trim();
  return /^#[0-9a-fA-F]{6}$/.test(h) ? h.toUpperCase() : fallback;
}

function hashName(name: string): number {
  let h = 2166136261;
  for (let i = 0; i < name.length; i++) {
    h ^= name.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function mix(hex: string, toward: string, t: number): string {
  const parse = (x: string) => [
    parseInt(x.slice(1, 3), 16),
    parseInt(x.slice(3, 5), 16),
    parseInt(x.slice(5, 7), 16),
  ] as const;
  const a = parse(hex);
  const b = parse(toward);
  const ch = (i: number) =>
    Math.round(a[i] + (b[i] - a[i]) * t)
      .toString(16)
      .padStart(2, "0");
  return `#${ch(0)}${ch(1)}${ch(2)}`;
}

/** Distinctive 512×512 SVG mark — letter + geometric seal from the brand name. */
export function generateStorefrontLogoSvg(
  name: string,
  accent = "#FF6B35",
): string {
  const brand = (name.trim() || "C").slice(0, 24);
  const letter = brand[0]!.toUpperCase();
  const accentHex = clampHex(accent, "#FF6B35");
  const deep = mix(accentHex, "#1A1812", 0.45);
  const light = mix(accentHex, "#FFF8EB", 0.35);
  const h = hashName(brand);
  const style = h % 4;
  const uid = `lg${(h % 1_000_000).toString(16)}`;

  let seal = "";
  if (style === 0) {
    seal = `<rect x="56" y="56" width="400" height="400" rx="96" fill="url(#${uid}g)"/>`;
  } else if (style === 1) {
    seal = `<circle cx="256" cy="256" r="196" fill="url(#${uid}g)"/>`;
  } else if (style === 2) {
    seal = `<path d="M256 48 L432 152 V360 L256 464 L80 360 V152 Z" fill="url(#${uid}g)"/>`;
  } else {
    seal = `<path d="M256 64 C320 64 400 96 424 176 C448 256 424 352 344 416 C280 464 232 464 168 416 C88 352 64 256 88 176 C112 96 192 64 256 64 Z" fill="url(#${uid}g)"/>`;
  }

  return `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" width="512" height="512" role="img" aria-label="${escapeXml(brand)} logo">
  <defs>
    <linearGradient id="${uid}g" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0%" stop-color="${light}"/>
      <stop offset="55%" stop-color="${accentHex}"/>
      <stop offset="100%" stop-color="${deep}"/>
    </linearGradient>
  </defs>
  <rect width="512" height="512" rx="96" fill="#FAFAF7"/>
  ${seal}
  <text x="256" y="300" text-anchor="middle" font-family="Georgia, 'Times New Roman', serif" font-size="220" font-weight="700" fill="#FFF8EB">${escapeXml(letter)}</text>
</svg>
`;
}

function escapeXml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

export function logoPrompt(name: string, accent: string, tagline?: string): string {
  return [
    `App icon / brand mark for "${name}", a travel eSIM storefront.`,
    tagline ? `Tagline: ${tagline}.` : "",
    `Flat vector, single centered symbol, no letters unless they are a refined monogram of "${name[0] ?? "C"}".`,
    `Color: ${accent} on warm cream #FAFAF7. Soft squircle icon, premium, simple, high contrast.`,
    `No photorealism, no mockups, no shadows of devices, no watermarks.`,
  ]
    .filter(Boolean)
    .join(" ");
}

export async function generateStorefrontLogo(
  input: { name: string; accent?: string; tagline?: string; env?: Record<string, string | undefined> },
): Promise<LogoMark> {
  const name = input.name.trim() || "Carrier";
  const accent = clampHex(input.accent ?? "#FF6B35", "#FF6B35");
  const svg = generateStorefrontLogoSvg(name, accent);
  return { svg, filename: "logo.svg", source: "svg" };
}
